import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Input,
  Space,
  Typography,
  Tag,
  Spin,
  message,
  Drawer,
  Tabs,
  Badge,
  Descriptions,
  Empty,
  Tooltip,
  Button,
} from 'antd';
import {
  SearchOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  SettingOutlined,
  ApiOutlined,
  EnvironmentOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LinkOutlined,
  ReloadOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiService } from '../../services/api';
import {
  Product,
  Entitlement,
  OrgEntitlement,
  SKU,
  ProvisioningQuestion,
  ProductAllowedRegion,
  ProductAddonConfig,
} from '../../types';
import { exportCommercialSKUs, hasCompleteDevTestFlags } from '../../services/skuExportService';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// ─────────────────────────────────────────────────────────────────────────────
// Internal types for the joined/denormalised data shapes
// ─────────────────────────────────────────────────────────────────────────────

interface SKULink {
  sku_id: number;
  sku_name: string;
  buying_model?: string;
  is_external: boolean;
  operation?: string;
  operand?: number;
  force_region_name?: string;
  entitlements: Entitlement[];   // entitlements linked via product_sku_config_entitlements
  orgEntitlements: OrgEntitlement[]; // org-entitlements linked via sku_org_entitlement_configs
}

interface ProductRow extends Product {
  skuLinks: SKULink[];
  entitlements: Entitlement[];
  orgEntitlements: OrgEntitlement[];
  allowedRegions: string[];
  provisioningQuestions: ProvisioningQuestion[];
}

interface SKUDrawerData {
  sku: SKU;
  linkedProducts: Array<{
    product: Product;
    operation?: string;
    operand?: number;
    force_region_name?: string;
    entitlements: Entitlement[];
  }>;
  orgEntitlements: OrgEntitlement[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Raw API shapes (before joins)
// ─────────────────────────────────────────────────────────────────────────────

interface RawSkuConfig {
  sku_id: number;
  product_id: number;
  operation?: string;
  operand?: number;
  force_region_name?: string;
}

interface RawSkuConfigEntitlement {
  product_sku_config_sku_id: number;
  product_sku_config_product_id: number;
  entitlement_id: number;
}

interface RawSkuOrgEntitlementConfig {
  sku_id: number;
  org_entitlement_id: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Bool icon helper
// ─────────────────────────────────────────────────────────────────────────────

const BoolIcon: React.FC<{ value: boolean }> = ({ value }) =>
  value
    ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
    : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />;

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const DataRelationships: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [productRows, setProductRows] = useState<ProductRow[]>([]);
  const [allSkus, setAllSkus] = useState<SKU[]>([]);
  const [allOrgEntitlements, setAllOrgEntitlements] = useState<OrgEntitlement[]>([]);
  const [allSkuOrgConfigs, setAllSkuOrgConfigs] = useState<RawSkuOrgEntitlementConfig[]>([]);
  const [allProductAddonConfigs, setAllProductAddonConfigs] = useState<ProductAddonConfig[]>([]);
  const [exportingSkus, setExportingSkus] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState<SKUDrawerData | null>(null);

  const loadingRef = useRef(false);

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadAll = async () => {
    if (loadingRef.current) { return; }
    loadingRef.current = true;
    setLoading(true);

    try {
      const [
        products,
        entitlements,
        orgEntitlements,
        skus,
        skuConfigs,
        skuConfigEntitlements,
        skuOrgConfigs,
        productAddonConfigs,
        allowedRegionsRaw,
        provisioningQuestionsRaw,
      ] = await Promise.all([
        apiService.getProducts(),
        apiService.getEntitlements(),
        apiService.getOrgEntitlements(),
        apiService.getSKUs(),
        apiService.getProductSkuConfigs() as Promise<RawSkuConfig[]>,
        apiService.getProductSkuConfigEntitlements() as Promise<RawSkuConfigEntitlement[]>,
        apiService.getSKUOrgEntitlementConfigs() as Promise<RawSkuOrgEntitlementConfig[]>,
        apiService.getProductAddonConfigs(),
        apiService.getProductSkuConfigs().then(() =>
          // We fetch allowed regions per product in the join below
          // Use a lightweight workaround: get from all product_sku_configs product_ids
          Promise.resolve([] as ProductAllowedRegion[])
        ),
        apiService.getAllProvisioningQuestions(),
      ]);

      // Fetch allowed regions for all products in one call if available,
      // otherwise fall back to empty (will just show empty list)
      let allowedRegionsAll: ProductAllowedRegion[] = [];
      try {
        // The API filters by product_id; since we want all, hit without filter
        const raw = await (apiService as unknown as {
          client?: { get: (url: string) => Promise<{ data: { data: ProductAllowedRegion[] } }> };
        }).client?.get('config/product_allowed_regions');
        allowedRegionsAll = (raw?.data?.data as ProductAllowedRegion[] | undefined) ?? [];
      } catch {
        allowedRegionsAll = [];
      }

      void allowedRegionsRaw; // suppress unused warning

      setAllSkus(skus ?? []);
      setAllOrgEntitlements(orgEntitlements ?? []);
      setAllSkuOrgConfigs(skuOrgConfigs ?? []);
      setAllProductAddonConfigs(productAddonConfigs ?? []);

      // ── Build ProductRow[] ───────────────────────────────────────────────

      const rows: ProductRow[] = (products ?? []).map((product) => {
        const productId = product.id ?? 0;

        // SKU links for this product
        const productConfigs = (skuConfigs ?? []).filter(
          (c) => c.product_id === productId,
        );
        const skuLinks: SKULink[] = productConfigs.map((cfg) => {
          const sku = (skus ?? []).find((s) => s.id === cfg.sku_id);
          const linkedEntitlementIds = (skuConfigEntitlements ?? [])
            .filter(
              (e) =>
                e.product_sku_config_sku_id === cfg.sku_id &&
                e.product_sku_config_product_id === productId,
            )
            .map((e) => e.entitlement_id);
          const linkedEntitlements = (entitlements ?? []).filter((e) =>
            e.id !== undefined && linkedEntitlementIds.includes(e.id),
          );

          // Org entitlements linked to this SKU via sku_org_entitlement_configs
          const linkedOrgEntIds = (skuOrgConfigs ?? [])
            .filter((c) => c.sku_id === cfg.sku_id)
            .map((c) => c.org_entitlement_id);
          const linkedOrgEntitlements = (orgEntitlements ?? []).filter(
            (oe) => oe.id !== undefined && linkedOrgEntIds.includes(oe.id),
          );

          return {
            sku_id: cfg.sku_id,
            sku_name: sku?.name ?? `SKU ${cfg.sku_id}`,
            buying_model: (sku as unknown as Record<string, string>)
              ?.subscription_buying_model,
            is_external: (sku as unknown as Record<string, boolean>)
              ?.is_external ?? false,
            operation: cfg.operation,
            operand: cfg.operand,
            force_region_name: cfg.force_region_name,
            entitlements: linkedEntitlements,
            orgEntitlements: linkedOrgEntitlements,
          };
        });

        // Entitlements owned by this product
        const productEntitlements = (entitlements ?? []).filter(
          (e) => e.product_id === productId,
        );

        // OrgEntitlements owned by this product
        const productOrgEntitlements = (orgEntitlements ?? []).filter(
          (e) => e.product_id === productId,
        );

        // Allowed regions
        const productRegions = (allowedRegionsAll ?? [])
          .filter((r) => r.product_id === productId)
          .map((r) => r.region_name);

        // Provisioning questions
        const questions = (provisioningQuestionsRaw ?? []).filter(
          (q) => q.product_id === productId,
        );

        return {
          ...product,
          skuLinks,
          entitlements: productEntitlements,
          orgEntitlements: productOrgEntitlements,
          allowedRegions: productRegions,
          provisioningQuestions: questions,
        };
      });

      setProductRows(rows);
    } catch {
      message.error('Failed to load relationship data');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // ── Search filter ─────────────────────────────────────────────────────────

  const filteredRows = productRows.filter((row) => {
    if (!searchText) { return true; }
    const q = searchText.toLowerCase();
    return (
      row.name?.toLowerCase().includes(q) ||
      row.key?.toLowerCase().includes(q) ||
      row.provisioning_client?.toLowerCase().includes(q) ||
      row.skuLinks.some((s) => s.sku_name?.toLowerCase().includes(q)) ||
      row.entitlements.some(
        (e) =>
          e.name?.toLowerCase().includes(q) || e.value?.toLowerCase().includes(q),
      ) ||
      row.orgEntitlements.some(
        (e) =>
          e.name?.toLowerCase().includes(q) || e.value?.toLowerCase().includes(q),
      )
    );
  });

  // ── SKU Drawer ────────────────────────────────────────────────────────────

  const openSkuDrawer = (skuId: number) => {
    const sku = allSkus.find((s) => s.id === skuId);
    if (!sku) { return; }

    // All products this SKU is linked to
    const linkedProducts = productRows
      .filter((p) => p.skuLinks.some((l) => l.sku_id === skuId))
      .map((p) => {
        const link = p.skuLinks.find((l) => l.sku_id === skuId) as SKULink;
        return {
          product: p as Product,
          operation: link.operation,
          operand: link.operand,
          force_region_name: link.force_region_name,
          entitlements: link.entitlements,
        };
      });

    // OrgEntitlements this SKU is linked to
    const skuOrgEntIds = allSkuOrgConfigs
      .filter((c) => c.sku_id === skuId)
      .map((c) => c.org_entitlement_id);
    const skuOrgEntitlements = allOrgEntitlements.filter((oe) =>
      oe.id !== undefined && skuOrgEntIds.includes(oe.id),
    );

    setDrawerData({ sku, linkedProducts, orgEntitlements: skuOrgEntitlements });
    setDrawerOpen(true);
  };

  const handleExportProductionSKUs = async () => {
    if (!hasCompleteDevTestFlags(allSkus)) {
      message.error('Production SKU export is unavailable until the SKU API returns is_dev_test for every SKU. Refresh after the backend change is deployed.');
      return;
    }
    const productionSkus = allSkus.filter((sku) => sku.is_dev_test === false);
    if (productionSkus.length === 0) {
      message.warning('No production SKUs are available to export.');
      return;
    }
    setExportingSkus(true);
    try {
      const result = await exportCommercialSKUs({
        products: productRows,
        skus: allSkus,
        addonConfigs: allProductAddonConfigs,
      });
      message.success(`Exported ${result.exportedCount} commercially available SKUs. Excluded ${result.excludedDevTestCount} development/test SKUs.`);
    } catch {
      message.error('Could not create the production SKU workbook. Please refresh the relationship data and try again.');
    } finally {
      setExportingSkus(false);
    }
  };

  // ── Expandable row content ────────────────────────────────────────────────

  const expandedRowRender = (record: ProductRow) => {
    const skuLinkCols: ColumnsType<SKULink> = [
      {
        title: 'SKU',
        dataIndex: 'sku_name',
        key: 'sku_name',
        render: (name: string, row: SKULink) => (
          <Button
            type="link"
            size="small"
            icon={<LinkOutlined />}
            onClick={() => openSkuDrawer(row.sku_id)}
            style={{ padding: 0 }}
          >
            {name}
          </Button>
        ),
      },
      {
        title: 'Buying Model',
        dataIndex: 'buying_model',
        key: 'buying_model',
        render: (v?: string) =>
          v ? <Tag color="blue">{v}</Tag> : <Text type="secondary">—</Text>,
      },
      {
        title: 'Is External',
        dataIndex: 'is_external',
        key: 'is_external',
        width: 100,
        render: (v: boolean) => <BoolIcon value={v} />,
      },
      {
        title: 'Operation',
        dataIndex: 'operation',
        key: 'operation',
        render: (v?: string) =>
          v ? <Tag color="orange">{v}</Tag> : <Text type="secondary">—</Text>,
      },
      {
        title: 'Operand',
        dataIndex: 'operand',
        key: 'operand',
        width: 90,
        render: (v?: number) => v ?? <Text type="secondary">—</Text>,
      },
      {
        title: 'Force Region',
        dataIndex: 'force_region_name',
        key: 'force_region_name',
        render: (v?: string) =>
          v ? <Tag color="cyan">{v}</Tag> : <Text type="secondary">—</Text>,
      },
      {
        title: 'Linked Entitlements',
        dataIndex: 'entitlements',
        key: 'entitlements',
        render: (ents: Entitlement[]) =>
          ents.length > 0 ? (
            <Space wrap>
              {ents.map((e) => (
                <Tooltip
                  key={e.id}
                  title={e.description || `value: ${e.value}`}
                >
                  <Tag color="green">{`${e.name}:${e.value}`}</Tag>
                </Tooltip>
              ))}
            </Space>
          ) : (
            <Text type="secondary">None</Text>
          ),
      },
      {
        title: 'Dependent Services',
        dataIndex: 'orgEntitlements',
        key: 'orgEntitlements',
        render: (oes: OrgEntitlement[]) =>
          oes.length > 0 ? (
            <Space wrap>
              {oes.map((oe) => (
                <Tooltip
                  key={oe.id}
                  title={`access: ${oe.access_type}${oe.description ? ` • ${oe.description}` : ''}`}
                >
                  <Tag color="purple">{`${oe.name}:${oe.value}`}</Tag>
                </Tooltip>
              ))}
            </Space>
          ) : (
            <Text type="secondary">None</Text>
          ),
      },
    ];

    const entitlementCols: ColumnsType<Entitlement> = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
      { title: 'Name', dataIndex: 'name', key: 'name', width: 220 },
      { title: 'Value', dataIndex: 'value', key: 'value', width: 180 },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        width: 280,
        ellipsis: true,
      },
      {
        title: 'Needs Qty',
        dataIndex: 'needs_quantity',
        key: 'needs_quantity',
        width: 90,
        render: (v: boolean) => <BoolIcon value={v} />,
      },
      { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 80 },
    ];

    const orgEntitlementCols: ColumnsType<OrgEntitlement> = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
      { title: 'Name', dataIndex: 'name', key: 'name', width: 220 },
      { title: 'Value', dataIndex: 'value', key: 'value', width: 180 },
      { title: 'Access Type', dataIndex: 'access_type', key: 'access_type', width: 130,
        render: (v: string) => <Tag color="purple">{v}</Tag> },
      {
        title: 'Is Service',
        dataIndex: 'is_service',
        key: 'is_service',
        width: 90,
        render: (v: boolean) => <BoolIcon value={v} />,
      },
      {
        title: 'Needs Qty',
        dataIndex: 'needs_quantity',
        key: 'needs_quantity',
        width: 90,
        render: (v: boolean) => <BoolIcon value={v} />,
      },
      { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 80 },
    ];

    const questionCols: ColumnsType<ProvisioningQuestion> = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
      { title: 'Name (Key)', dataIndex: 'name', key: 'name', width: 200 },
      { title: 'Display Name', dataIndex: 'display_name', key: 'display_name', width: 220 },
      { title: 'Type', dataIndex: 'type', key: 'type', width: 120,
        render: (v: string) => <Tag color="geekblue">{v}</Tag> },
      { title: 'Description', dataIndex: 'description', key: 'description', width: 280,
        ellipsis: true },
    ];

    const commonTableProps = {
      size: 'small' as const,
      bordered: true,
      pagination: false as const,
      style: { marginBottom: 0 },
    };

    return (
      <Tabs size="small" style={{ background: 'transparent' }}>
        {/* SKUs */}
        <TabPane
          tab={
            <span>
              <SettingOutlined />
              <Badge
                count={record.skuLinks.length}
                size="small"
                style={{ marginLeft: 4 }}
                color="blue"
              >
                <span style={{ paddingRight: 8 }}>SKUs</span>
              </Badge>
            </span>
          }
          key="skus"
        >
          {record.skuLinks.length === 0 ? (
            <Empty description="No SKUs linked to this product" />
          ) : (
            <Table
              {...commonTableProps}
              dataSource={record.skuLinks}
              columns={skuLinkCols}
              rowKey="sku_id"
            />
          )}
        </TabPane>

        {/* Entitlements */}
        <TabPane
          tab={
            <span>
              <UnorderedListOutlined />
              <Badge
                count={record.entitlements.length}
                size="small"
                style={{ marginLeft: 4 }}
                color="green"
              >
                <span style={{ paddingRight: 8 }}>Entitlements</span>
              </Badge>
            </span>
          }
          key="entitlements"
        >
          {record.entitlements.length === 0 ? (
            <Empty description="No entitlements for this product" />
          ) : (
            <Table
              {...commonTableProps}
              dataSource={record.entitlements}
              columns={entitlementCols}
              rowKey="id"
              scroll={{ x: 'max-content' }}
            />
          )}
        </TabPane>

        {/* Org Entitlements */}
        <TabPane
          tab={
            <span>
              <ApiOutlined />
              <Badge
                count={record.orgEntitlements.length}
                size="small"
                style={{ marginLeft: 4 }}
                color="purple"
              >
                <span style={{ paddingRight: 8 }}>Org Entitlements</span>
              </Badge>
            </span>
          }
          key="org_entitlements"
        >
          {record.orgEntitlements.length === 0 ? (
            <Empty description="No org entitlements for this product" />
          ) : (
            <Table
              {...commonTableProps}
              dataSource={record.orgEntitlements}
              columns={orgEntitlementCols}
              rowKey="id"
              scroll={{ x: 'max-content' }}
            />
          )}
        </TabPane>

        {/* Allowed Regions */}
        <TabPane
          tab={
            <span>
              <EnvironmentOutlined />
              <Badge
                count={record.allowedRegions.length}
                size="small"
                style={{ marginLeft: 4 }}
                color="cyan"
              >
                <span style={{ paddingRight: 8 }}>Regions</span>
              </Badge>
            </span>
          }
          key="regions"
        >
          {record.allowedRegions.length === 0 ? (
            <Empty description="No allowed regions configured" />
          ) : (
            <Space wrap style={{ padding: '8px 0' }}>
              {record.allowedRegions.map((r) => (
                <Tag key={r} color="cyan" icon={<EnvironmentOutlined />}>
                  {r}
                </Tag>
              ))}
            </Space>
          )}
        </TabPane>

        {/* Provisioning Questions */}
        <TabPane
          tab={
            <span>
              <QuestionCircleOutlined />
              <Badge
                count={record.provisioningQuestions.length}
                size="small"
                style={{ marginLeft: 4 }}
                color="gold"
              >
                <span style={{ paddingRight: 8 }}>Questions</span>
              </Badge>
            </span>
          }
          key="questions"
        >
          {record.provisioningQuestions.length === 0 ? (
            <Empty description="No provisioning questions" />
          ) : (
            <Table
              {...commonTableProps}
              dataSource={record.provisioningQuestions}
              columns={questionCols}
              rowKey="id"
              scroll={{ x: 'max-content' }}
            />
          )}
        </TabPane>
      </Tabs>
    );
  };

  // ── Top-level product columns ─────────────────────────────────────────────

  const productColumns: ColumnsType<ProductRow> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 65,
      sorter: (a, b) => (a.id ?? 0) - (b.id ?? 0),
    },
    {
      title: 'Product Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      ellipsis: true,
      render: (v?: string) =>
        v ? <Tag color="default">{v}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Provisioning Client',
      dataIndex: 'provisioning_client',
      key: 'provisioning_client',
      ellipsis: true,
      render: (v?: string) =>
        v ? <Tag color="blue">{v}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'SKUs',
      key: 'sku_count',
      width: 70,
      sorter: (a, b) => a.skuLinks.length - b.skuLinks.length,
      render: (_: unknown, row: ProductRow) => (
        <Badge count={row.skuLinks.length} color="blue" showZero />
      ),
    },
    {
      title: 'Entitlements',
      key: 'ent_count',
      width: 110,
      sorter: (a, b) => a.entitlements.length - b.entitlements.length,
      render: (_: unknown, row: ProductRow) => (
        <Badge count={row.entitlements.length} color="green" showZero />
      ),
    },
    {
      title: 'Org Entitlements',
      key: 'org_ent_count',
      width: 130,
      sorter: (a, b) => a.orgEntitlements.length - b.orgEntitlements.length,
      render: (_: unknown, row: ProductRow) => (
        <Badge count={row.orgEntitlements.length} color="purple" showZero />
      ),
    },
    {
      title: 'Regions',
      key: 'region_count',
      width: 80,
      render: (_: unknown, row: ProductRow) => (
        <Badge count={row.allowedRegions.length} color="cyan" showZero />
      ),
    },
    {
      title: 'Questions',
      key: 'question_count',
      width: 90,
      render: (_: unknown, row: ProductRow) => (
        <Badge
          count={row.provisioningQuestions.length}
          color="gold"
          showZero
        />
      ),
    },
    {
      title: 'Hidden',
      dataIndex: 'is_hidden',
      key: 'is_hidden',
      width: 75,
      render: (v: boolean) => <BoolIcon value={!!v} />,
    },
    {
      title: 'Auto Prov.',
      dataIndex: 'is_auto_provision',
      key: 'is_auto_provision',
      width: 90,
      render: (v: boolean) => <BoolIcon value={!!v} />,
    },
  ];

  // ── SKU Drawer content ────────────────────────────────────────────────────

  const renderDrawer = () => {
    if (!drawerData) { return null; }
    const { sku, linkedProducts, orgEntitlements: skuOrgEnts } = drawerData;

    return (
      <Drawer
        title={
          <Space>
            <SettingOutlined style={{ color: '#1890ff' }} />
            <span>SKU Detail: <Text strong>{sku.name}</Text></span>
          </Space>
        }
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={680}
        destroyOnClose
      >
        {/* SKU summary */}
        <Descriptions
          bordered
          size="small"
          column={2}
          style={{ marginBottom: 24 }}
        >
          <Descriptions.Item label="ID">{sku.id}</Descriptions.Item>
          <Descriptions.Item label="Name">{sku.name}</Descriptions.Item>
          <Descriptions.Item label="Buying Model">
            {(sku as unknown as Record<string, string>).subscription_buying_model
              ? (
                <Tag color="blue">
                  {(sku as unknown as Record<string, string>).subscription_buying_model}
                </Tag>
              )
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Is External">
            <BoolIcon value={!!(sku as unknown as Record<string, boolean>).is_external} />
          </Descriptions.Item>
        </Descriptions>

        {/* Linked Products */}
        <Title level={5} style={{ marginBottom: 12 }}>
          <AppstoreOutlined style={{ marginRight: 6 }} />
          Linked Products ({linkedProducts.length})
        </Title>

        {linkedProducts.length === 0 ? (
          <Empty description="No products linked to this SKU" />
        ) : (
          linkedProducts.map(({ product, operation, operand, force_region_name, entitlements }) => (
            <Card
              key={product.id}
              size="small"
              style={{ marginBottom: 12 }}
              title={
                <Space>
                  <Tag color="blue">ID: {product.id}</Tag>
                  <Text strong>{product.name}</Text>
                  {product.key && <Tag color="default">{product.key}</Tag>}
                </Space>
              }
            >
              <Space wrap style={{ marginBottom: 8 }}>
                {operation && (
                  <Tag color="orange">
                    Operation: {operation}
                    {operand !== undefined ? ` (${operand})` : ''}
                  </Tag>
                )}
                {force_region_name && (
                  <Tag color="cyan" icon={<EnvironmentOutlined />}>
                    Force: {force_region_name}
                  </Tag>
                )}
              </Space>

              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Linked Entitlements:
                </Text>
                <Space wrap style={{ marginTop: 4 }}>
                  {entitlements.length > 0 ? (
                    entitlements.map((e) => (
                      <Tooltip
                        key={e.id}
                        title={`value: ${e.value}${e.description ? ` • ${e.description}` : ''}`}
                      >
                        <Tag color="green">{e.name}</Tag>
                      </Tooltip>
                    ))
                  ) : (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      None
                    </Text>
                  )}
                </Space>
              </div>
            </Card>
          ))
        )}

        {/* Org Entitlements */}
        {skuOrgEnts.length > 0 && (
          <>
            <Title level={5} style={{ margin: '20px 0 12px' }}>
              <ApiOutlined style={{ marginRight: 6 }} />
              Org Entitlements ({skuOrgEnts.length})
            </Title>
            <Space wrap>
              {skuOrgEnts.map((oe) => (
                <Tooltip
                  key={oe.id}
                  title={`value: ${oe.value} • access: ${oe.access_type}${oe.description ? ` • ${oe.description}` : ''}`}
                >
                  <Tag color="purple">{oe.name}:{oe.value}</Tag>
                </Tooltip>
              ))}
            </Space>
          </>
        )}
      </Drawer>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <Title level={2} style={{ marginBottom: 4 }}>Data Relationships</Title>
              <Text type="secondary">
                Explore how Products, SKUs, Entitlements, Regions and Provisioning
                Questions are connected. Expand a product row to see its full
                relationship tree. Click any{' '}
                <LinkOutlined style={{ color: '#1890ff' }} /> SKU link to see
                the SKU&apos;s cross-product view.
              </Text>
            </div>
            <Space>
              <Tooltip title="Exports only SKUs where is_dev_test is false. Includes separate standalone, suite, and add-on sheets.">
                <Button
                  icon={<FileExcelOutlined />}
                  loading={exportingSkus}
                  disabled={loading || allSkus.length === 0}
                  onClick={() => void handleExportProductionSKUs()}
                >
                  Export Production SKUs
                </Button>
              </Tooltip>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  loadingRef.current = false;
                  void loadAll();
                }}
              >
                Refresh
              </Button>
            </Space>
          </div>

          {/* Search */}
          <Input
            placeholder="Search by product name, key, SKU name, entitlement name/value…"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            size="large"
            style={{ maxWidth: 620 }}
          />

          {/* Legend */}
          <Space wrap>
            <Text type="secondary" style={{ fontSize: 12 }}>Legend:</Text>
            <Tag color="blue">SKU count</Tag>
            <Tag color="green">Entitlements</Tag>
            <Tag color="purple">Org Entitlements</Tag>
            <Tag color="cyan">Regions</Tag>
            <Tag color="gold">Provisioning Questions</Tag>
            <Tag icon={<LinkOutlined />} color="default">Click SKU name → cross-product detail</Tag>
          </Space>

          {/* Main table */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <Spin size="large" tip="Building relationship graph…" />
            </div>
          ) : (
            <Table<ProductRow>
              dataSource={filteredRows}
              columns={productColumns}
              rowKey="id"
              expandable={{
                expandedRowRender,
                expandedRowKeys,
                onExpandedRowsChange: (keys) =>
                  setExpandedRowKeys(keys as React.Key[]),
                rowExpandable: () => true,
              }}
              pagination={{
                pageSize: 15,
                showSizeChanger: true,
                showTotal: (total) => `${total} product${total !== 1 ? 's' : ''}`,
              }}
              bordered
              size="small"
              scroll={{ x: 'max-content' }}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {filteredRows.length} product{filteredRows.length !== 1 ? 's' : ''} shown
                      {searchText ? ` matching "${searchText}"` : ''}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          )}
        </Space>
      </Card>

      {renderDrawer()}
    </div>
  );
};

export default DataRelationships;
